/**
 * Budget governance client API (Phase 6).
 *
 * Typed against the Phase 2/3 endpoints. Two rules the UI inherits from
 * the server contract and must not undo:
 *
 *  1. **Money crosses the wire as raw USD microdollars**, never
 *     pre-formatted and never pre-converted. Display currency travels
 *     alongside as `{displayCurrency, displayCurrencyRate}` and is applied
 *     at render time by `formatMoneyLabeled`. Converting server-side would
 *     bake a stale admin-set rate into stored-looking values.
 *  2. **`null` = unlimited and `0` = hard block are different states.**
 *     Every cap field below is `BudgetCap` (`number | null`) for that
 *     reason. A control that coerces blank→0 silently turns "unlimited"
 *     into "blocked", which is the difference between a working tenant
 *     and a dead one.
 */
import { request } from '@papan/api-client'
import type {
  BudgetCap, AttributionResult, DisplayRateSource, PlanAuthority,
  ManualBillingStatus, PlanSource, LandingExperience, LandingSource,
  ChatCostVisibility,
  TenantMode, TenantModeSource, PlatformModeSource,
  // Task 16: subscription-term cycle unit, shared with the price book's own.
  CycleUnit as SubscriptionCycleUnit,
  // P3 (0189) — per-tenant uploads + rate-limit overrides.
  PlanRateLimitTier,
} from '@papan/types'

/** Display-currency config echoed by every read endpoint. */
/**
 * The unambiguous per-axis override request (0177 F2 round).
 *
 * Blank/clear in an editor MUST send `{ state: 'inherit' }`. It must NOT
 * send `unlimited` (that is an explicit state on the FREE axis only),
 * `disabled` (extra only), or a missing field (which means "leave the
 * existing override unchanged").
 *
 * ── Per-axis TYPES, not a shared union (0177 follow-up) ──────────────────
 *
 * A shared `TenantOverrideState` union across all four axes was expressible
 * as `storage = { state: 'unlimited' }` — which the DB CHECK rejects and
 * the route 400s. TypeScript could not spot that until runtime. Each axis
 * declares its OWN allowed set below, so an editor sending an invalid
 * combination is a COMPILE error, and the DTO the client offers matches
 * exactly the DTO the server accepts.
 *
 * Correspondence: state → DB CHECK constraint → route parser.
 *
 *   storage      finite | inherit                 teo_axis_state_chk
 *   concurrency  finite | inherit                 teo_axis_state_chk
 *   free         finite | unlimited | inherit    teo_axis_state_chk
 *   extra        finite | disabled  | inherit    teo_axis_state_chk
 */
export type TenantStorageOverride =
  | { state: 'finite'; value: number }
  | { state: 'inherit' }
export type TenantConcurrencyOverride =
  | { state: 'finite'; value: number }
  | { state: 'inherit' }
export type TenantFreeCapOverride =
  | { state: 'finite'; value: number }
  | { state: 'unlimited' }
  | { state: 'inherit' }
export type TenantExtraCapOverride =
  | { state: 'finite'; value: number }
  | { state: 'disabled' }
  | { state: 'inherit' }
/**
 * The grace axis (Task 3) — `finite | inherit` only, mirroring
 * `AXIS_STATES.grace = ['finite']` at the DB layer (no `unlimited` /
 * `disabled`: an unlimited grace override would decode to `null` = inherit
 * under the platform clamp, a meaningless state).
 */
export type TenantGraceCapOverride =
  | { state: 'finite'; value: number }
  | { state: 'inherit' }

/**
 * Legacy pre-round type kept for FEWER downstream churn: was a common union
 * covering every state on every axis. Prefer the axis-specific types above.
 * @deprecated Use axis-specific override types.
 */
export type TenantOverrideState =
  | TenantStorageOverride
  | TenantFreeCapOverride
  | TenantExtraCapOverride

/** The projected effective ceilings after a `setTenantOverrides` write. */
export interface EffectiveCeilings {
  freeCapBillingMicro: number | null
  extraCapBillingMicro: number
  agentConcurrencyCap: number
  storageQuotaBytes: number
  graceCapBillingMicro: number | null
}

export interface CurrencyConfig {
  displayCurrency: string
  displayCurrencyRate: number
  /** Which step of the chain produced the rate — `derived` means it tracks
   *  `billing_settings.credit_rate_minor_per_usd` and needs no maintenance,
   *  `explicit` means an operator pinned it. Display-only; the rate itself is
   *  already resolved. */
  displayCurrencyRateSource?: DisplayRateSource
}

/** Per-bucket spend for a period, in microdollars, in waterfall order.
 *  `promo` (C1) is spend funded by drawable promotional credit — it drains
 *  before `free` and never consumes the included allowance. `prepaid` (0141)
 *  is spend funded by the tenant's prepaid usage credits, drawn after the
 *  included allowance is gone (included credit expires at renewal, credits
 *  don't). Both are optional because older API responses omit them. */
export interface BucketUsage {
  promo?: number
  /** Prepaid credit the tenant BOUGHT (plan §9). Optional for the same reason
   *  as `promo` — an older API omits it. Distinct from `promo` because it is
   *  the customer's own money: it counts against app-owner ceilings, is
   *  refundable, and is never revocable by a platform admin. */
  purchased?: number
  free: number
  prepaid?: number
  extra: number
  grace: number
  total: number
  /**
   * `total` in the display currency's MINOR units, converted per row at the
   * rate in force when each row was incurred (0172).
   *
   * `null` = canonical USD, nothing was converted. Optional because an older
   * API omits it entirely, same reason as `promo` and `prepaid`.
   *
   * There is no per-BUCKET display figure on the wire: the buckets are rendered
   * as canonical USD tiles, and only the totals are converted. A per-bucket set
   * would have to round each one and then sum the rounded values to keep the
   * total honest — worth doing if those tiles ever go non-USD, and deliberately
   * not done speculatively.
   */
  totalDisplayMinor?: number | null
}

/** The individual cap components enforcement summed — used to explain a
 *  pooled ceiling rather than just assert it. */
export interface CapComponents {
  roleCap: BudgetCap
  teamSum: number
  adjust: number
}

export interface MemberUsageRow {
  email: string
  role: string
  /**
   * A PLATFORM SUPPORT SEAT (0180) — platform staff seated here to maintain
   * something the platform provides. Changes no permission; it means the AI
   * turns and compute this person runs in this tenant bill the platform org
   * rather than the customer.
   */
  platformSeat?: boolean
  /**
   * Is the MARKER on the membership row, whatever it is currently doing?
   *
   * `platformSeat` above is the honest claim — marker AND platform-admin
   * status, the pair `resolvePlatformPayer` enforces — and is what any badge
   * should render. This is the raw marker, and the two diverge for a seated
   * person who has since left the platform team: nothing reaps the column, so
   * the seat bills nobody and must still be REVOCABLE. A control driven off
   * the badge offers "Mark as support seat" there, which the grant guard
   * refuses (400 `NOT_PLATFORM_ADMIN`) — leaving the marker live to re-arm if
   * the person is ever re-promoted. Drive actions off this one.
   */
  seatMarked?: boolean
  /**
   * Does this member's email confer platform admin? Console-only (set by
   * `GET /api/admin/tenants/:id/usage`, absent from the customer's own
   * dashboard). Gates the "Remove from tenant" action — only platform staff are
   * removable from the console; the server refuses anyone else. Absent ⇒ treat
   * as false (not removable).
   */
  platformStaff?: boolean
  usage: BucketUsage
  caps: {
    userCap: BudgetCap
    components: CapComponents
  }
}

export interface TeamUsageRow {
  id: string
  name: string
  /**
   * Parent team (#770); `null` = a root team. Optional for the rolling deploy:
   * an older API omits it, and every team then renders as a root — the flat
   * list this replaced, never a missing row.
   */
  parentId?: string | null
  /** Sibling order, highest first — the Teams page's drag order. */
  priority?: number
  /** Contribution, so 0 means "contributes nothing" — NOT unlimited. */
  cap: number
  /**
   * Spend by the members of this team AND of every sub-team beneath it, each
   * member once — `buildOrgUsage` rolls a member's spend into every team that
   * funds them, ancestors included, over a deduplicated team set. So a
   * parent's figure already contains its sub-teams'; never sum a subtree.
   */
  used: number
  /** `used` in display-currency minor units; `null` = canonical USD (0172).
   *  Rolls up the ROUNDED member figures, so a team's displayed spend is
   *  exactly the sum of the rows shown beneath it. */
  usedDisplayMinor?: number | null
}

// `StorageUsage` lived here to type a `storage` block on the org usage
// response. That block was removed when Settings → Storage took over drive
// utilisation, and the interface outlived its last consumer. Storage figures
// now come from `storageApi.getComposition`, which types its own response —
// there is no second spelling of the shape to keep in step.

/**
 * One metered surface's spend for a period (Phase 7).
 *
 * `cost` is microdollars like every other money field. `cost === 0` with a
 * non-zero `quantity` is the metered-but-free state (`feature_pricing
 * .chargeable = false`), NOT missing data — the UI must still show the
 * utilization. `quantity` is 0 for `llm`: that ledger prices per model and
 * carries no comparable unit count.
 */
export interface FeatureSpend {
  feature: string
  cost: number
  quantity: number
  /** `cost` in display-currency minor units; `null` = canonical USD (0172).
   *  Rounded per feature — a leaf — so a caller summing these gets a total that
   *  agrees with the rows it is shown beside. */
  costDisplayMinor?: number | null
}

/** Platform-global charging config for one metered surface. */
export interface FeaturePricingRow {
  feature: string
  chargeable: boolean
  /** Microdollars per `unit`. */
  unitRateUsd: number
  unit: string
}

/** One graduated pricing tier: units up to `uptoQty` (null = unbounded, only
 *  the last tier) price at `rateUsd` microdollars/unit. */
export interface PricingTier {
  uptoQty: number | null
  rateUsd: number
}

/** A per-tenant pricing override for one feature. `chargeable`/`baseRateUsd`
 *  null = inherit the global default for that axis. */
export interface TenantPricingOverride {
  feature: string
  mode: 'flat' | 'tiered'
  chargeable: boolean | null
  baseRateUsd: number | null
  tiers: PricingTier[]
}

/** The resolved pricing view for one feature at one level (tenant or plan):
 *  global default, the override at this level (or null), and which is in effect.
 *
 *  `planLayer` is populated ONLY by the per-tenant read — it is the middle link
 *  of the tenant → plan → global chain, resolved for the tenant's actual plan.
 *  Without it the editor cannot tell "inherits the platform rate" apart from
 *  "inherits a different rate via its plan", which are different prices. */
export interface TenantFeaturePricingRow {
  feature: string
  global: FeaturePricingRow | null
  override: TenantPricingOverride | null
  planLayer?: TenantPricingOverride | null
  source: 'override' | 'plan' | 'global'
}

/** Per-role cap override as stored. An ABSENT role means "no override",
 *  which falls back to the hardcoded per-role default — not the same as an
 *  override of 0. Single-axis (free/extra split removed); carries both the
 *  window-keyed JSONB and the legacy int4 scalar during the expand window;
 *  the JSONB wins when present. */
export interface RoleCapEntry {
  cap?: { month?: number | null }
  capBillingMicro?: number
}

/** Tier-2: what an org admin sees. */
/** One org-layer model block. Its presence is the restriction. */
export interface ModelRestrictionRow {
  modelId: string
  subjectType: 'role' | 'team' | 'user'
  /** role name | team id | lowercase email */
  subjectId: string
}

export interface OrgUsageResponse {
  period: string
  enforcementMode: 'enforce' | 'warn_only'
  currency: CurrencyConfig
  tenant: {
    usage: BucketUsage
    caps: { freeCap: BudgetCap; extraCap: BudgetCap }
    /**
     * Bucket APPLICABILITY (not spend). A funding source is "applicable" when
     * this tenant has it, so the "Spend by bucket" tile shows 0.00 rather than
     * '—'. `free` is always applicable (no flag). Optional for rolling deploy:
     * an older API omits them, and the UI then shows the value (never a wrong
     * '—'). Tenant-level, so they also govern the member self-view's tiles.
     */
    promoApplicable?: boolean
    prepaidApplicable?: boolean
  }
  users: MemberUsageRow[]
  teams: TeamUsageRow[]
  // No `storage`: it fed the dashboard's storage bar, which Settings → Storage
  // replaced. Removed from the TYPE as well as the route, so any future
  // consumer fails to compile rather than reading `undefined`.
  /** Per-metered-surface spend for the period. */
  featureBreakdown: FeatureSpend[]
  /** Read-back for the budgets editor. `null` = never configured, so the
   *  documented defaults (`pct [80,100]`) apply. Without this the editor
   *  is write-only and every save blindly replaces the whole object. */
  thresholds: { pct?: number[]; absUsd?: number[] } | null
  /** Read-back for the role-cap editor, keyed by role name. */
  roleCaps: Record<string, RoleCapEntry> | null
}

/** Tier-3: what a member sees about themselves. */
export interface MyUsageResponse {
  period: string
  email: string
  role: string
  enforcementMode: 'enforce' | 'warn_only'
  /**
   * Is this viewer on a platform support seat HERE (0180)?
   *
   * When true, every other figure on this response describes a wallet that is
   * not theirs: their spend is payer-keyed and so reads 0, while the caps,
   * pooled ceiling and attribution waterfall are the CUSTOMER's, and their
   * turns are enforced against the platform org's. The page must say so —
   * it cannot show the platform's numbers instead, because this caller holds
   * no capability in that tenant.
   */
  platformSeat?: boolean
  currency: CurrencyConfig
  usage: BucketUsage
  /** Bucket applicability (tenant-level) — see `OrgUsageResponse.tenant`. Lets a
   *  member's own bucket tiles show 0.00 vs '—'. Optional for rolling deploy. */
  promoApplicable?: boolean
  prepaidApplicable?: boolean
  caps: {
    userCap: BudgetCap
    tenantFreeCap: BudgetCap
    tenantExtraCap: BudgetCap
    pooled: BudgetCap
  }
  /** Per-source waterfall + which source funds the next dollar. */
  attribution: AttributionResult
  /** Where the CALLER's own spend went, by metered feature (A5). Self-scoped
   *  server-side from the auth context — the endpoint takes no email
   *  parameter, which is what stops it reading a colleague's activity. Same
   *  shape and same aggregation as the org dashboard's, so the two reconcile.
   *
   *  No `storage` row, and the reason is NOT the one this used to give. It
   *  said the storage meter writes tenant-level rows with no `user_email`;
   *  migration 0171 removed that meter entirely. Storage is a QUOTA, enforced
   *  against `tenants.storage_quota_bytes`, writing no ledger row and carrying
   *  no price — so there is nothing here to attribute to anyone. Anyone
   *  re-metering it should read that as: nothing in this type is holding it
   *  back. */
  featureBreakdown: FeatureSpend[]
  teamIds: string[]
  // No `storage` — see OrgUsageResponse. Nothing rendered it, and computing it
  // cost four aggregates on the endpoint every member now reaches.
}

/** Tier-1 row: one tenant, as the platform admin sees it. */
export interface AdminTenantRow {
  id: string
  name: string
  type: string
  /** Personal tenant: the account's main email. Absent on older responses. */
  primaryEmail?: string | null
  /** `false` = the account never proved its main email (shown as Unverified). */
  primaryEmailVerified?: boolean | null
  /** Organization: its owners' emails, oldest first. Absent on older responses. */
  ownerEmails?: string[]
  /** Organization: owners invited and not yet joined (open invites). */
  invitedOwnerEmails?: string[]
  /** Personal: how many OTHER verified emails the account has (count only). */
  otherEmailCount?: number
  /** With an email search: the address on this tenant that matched it. */
  matchedEmail?: string | null
  freeCapBillingMicro: BudgetCap
  extraCapBillingMicro: BudgetCap
  /** `null` = fall back to the global default (3), NOT unlimited. */
  agentConcurrencyCap: number | null
  enforcementMode: 'enforce' | 'warn_only'
  periodCostBillingMicro: number
  periodMessages: number
  /** Per-metered-surface spend for this tenant in the period. */
  featureBreakdown: FeatureSpend[]
  storageQuotaBytes: number
  /** `null` = never measured (cache miss), which is not the same as 0. */
  storageUsedBytes: number | null
  /**
   * Engine "scratch" bytes (#535) — app-db working-set space the Phase 2
   * survey measures per tenant. DISPLAY-ONLY: never folded into
   * `storageUsedBytes`/`storageQuotaBytes` or any counted/enforced figure.
   * `0` both when never surveyed and when genuinely empty. Optional for the
   * same rolling-deploy reason as `maxSeatsOverride` above — an older API
   * may not send it yet.
   */
  scratchBytes?: number
  /** This tenant's own seat ceiling. `null` = INHERIT the plan's `max_seats`
   *  (which may itself be null = unlimited). `0` is a real value meaning
   *  nobody. Never compare it alone — `resolveMaxSeats(override, planValue)`
   *  is the ladder, and `resolveSeatCap` is the server-side resolver the
   *  membership gate refuses on. */
  //
  // OPTIONAL for a rolling deploy, like every other field this branch adds: the
  // SPA and the API ship separately, so a newer console can meet an older API
  // that does not send it. `CapInput`'s `capToText` special-cases `null` only —
  // an `undefined` seeds the box with the literal string "undefined", which
  // then fails validation on blur.
  maxSeatsOverride?: number | null
  /** `tenant_settings.included_seats` — the per-tenant included-seats override
   *  (0180). `null`/absent = inherit the plan. Under manual billing this is the
   *  tenant's seat ceiling. Optional for the same rolling-deploy reason. */
  includedSeatsOverride?: number | null
  //
  // The RESOLVED ceiling is deliberately NOT on this row. Resolving it needs
  // the tenant's plan (`resolveSeatCap` → `resolveTenantPlanId` → the plan's
  // `max_seats`), which is one extra resolve PER ROW on a paged customers list.
  // The PUT response reports what the write resolved to, which is the moment an
  // operator actually needs it.
  /** The RESOLVED pair — explicit → derived → env → USD@1 — so a row renders
   *  the same figures the tenant's own dashboard does. */
  displayCurrency: string
  displayCurrencyRate: number
  displayCurrencyRateSource: DisplayRateSource
  /**
   * What clearing the override would yield, so the editor can offer the derived
   * mode as a concrete rate. `null` = the deployment's credit rate cannot
   * honestly produce one for this tenant's currency (it charges in a different
   * one), and the editor hides the option rather than promising a number the
   * server would not use.
   */
  derivedDisplayCurrencyRate: number | null
  /**
   * This tenant's chat-cost override AS STORED (0174). `null` = follows the
   * platform setting.
   *
   * Deliberately not resolved: the fallback is per-org and then clamped per
   * viewer, so the console shows the inherit state as its own option rather
   * than printing a value this tenant's members may not actually see. Optional
   * for rolling-deploy tolerance.
   */
  chatCostVisibility?: ChatCostVisibility | null
  /**
   * Models this tenant may use at all. `null` = unrestricted (the whole
   * catalog). Excluded models are HIDDEN from the tenant's model picker, not
   * shown as restricted — see docs/plans/2026-07-30_MODEL_ACCESS_GOVERNANCE.md.
   */
  allowedModels: string[] | null
  /** Effective plan. Never null: a tenant with no live subscription is on the
   *  free plan, which is the implicit fallback rather than an absence. */
  planId: string | null
  planName: string | null
  /**
   * Which layer decided the plan (0157). `override` means an operator set it by
   * hand and it exists only here — the list badges that, because a plan the
   * payment provider has never heard of should not look identical to one it
   * bills for. Optional for rolling-deploy tolerance.
   */
  planSource?: 'subscription' | 'override' | 'free'
  /**
   * The customer's plan pauses NEW items — manual billing's write freeze.
   *
   * Resolved server-side by `planBlocksCreate`, the same predicate the gate
   * enforces, and already false on a deployment where plans mean nothing. It is
   * deliberately SEPARATE from `health`: that badge is about AI and payment, this
   * is about uploads, and a tenant can be in either state, both, or neither.
   * Optional for rolling-deploy tolerance.
   */
  uploadsPaused?: boolean
  /**
   * This tenant's OWN request-limit override (`tenant_settings.rate_limits`),
   * or `null`/absent when it inherits the plan's. The per-tenant Plan tab
   * (A15) overlays this onto the plan handed to `PlanRateLimitsEditor` so a
   * saved override renders instead of the plan's default. Optional for
   * rolling-deploy tolerance.
   */
  rateLimits?: Partial<Record<'db' | 'sdk' | 'scripts', number>> | null
  /**
   * What to DISPLAY as this customer's plan (0164) — the preset's name, "Custom",
   * or the operator's own label. Distinct from `planName`, which is always the
   * PRESET's name: a Custom tenant shows a different string while still pointing
   * at that preset, and conflating the two is the trap `plan-preset.ts` names.
   */
  planLabel?: string
  planLabelSource?: 'preset' | 'custom-named' | 'custom'
  /** Hand-edited since seeding. NOT a claim that the tenant differs from its
   *  preset — a preset can move without the tenant being propagated. */
  isCustom?: boolean
  /** ISO. The list has always been ordered by it; now it is also shown, so a new
   *  customer is stated rather than inferred from "0 members". */
  createdAt?: string
  /**
   * The RESOLVED preset's own values, so Configuration can mark which of this
   * customer's numbers were hand-set rather than inherited. `null` when the
   * preset pointer names a deleted plan.
   *
   * `freeCapBillingMicro` is already multiplied by seat count — the preset stores it PER
   * SEAT, and comparing that against a tenant total would mark everyone as
   * diverging.
   */
  presetCaps?: {
    freeCapBillingMicro: number
    extraCapBillingMicro: number | null
    storageQuotaBytes: number
    agentConcurrencyCap: number | null
    /**
     * The plan's grace pool — the INHERITANCE BASELINE an uncustomized
     * tenant on this plan would carry. `null` when the preset has never been
     * priced with one (`0 ⇒ null`, mirroring `free`/`extra`: a genuine zero
     * pool would silently disable grace for every tenant on the plan).
     * Deliberately NOT seat-scaled, unlike `freeCapBillingMicro` above — grace
     * is a flat plan property. Distinct from `platformGraceCeiling` below,
     * which is the deployment-wide clamp, not per-preset.
     */
    graceCapBillingMicro: number | null
  } | null
  /**
   * The deployment-wide grace CEILING (Task 4,
   * 2026-08-24_TENANT_PLAN_TAB_MODAL_REUSE) — the same clamp `cost-check.ts`
   * and the console's `/caps` route resolve through
   * `resolvePlatformGraceCapBillingMicro`, echoed identically on every row.
   * NOT the plan's own grace pool (`presetCaps.graceCapBillingMicro` above,
   * which varies per preset) — this is the single number every tenant's grace
   * is clamped against regardless of plan.
   */
  platformGraceCeiling?: number
  /**
   * This tenant's own per-axis override RECORDS (Task 4) — which axes are
   * hand-set, at what state/value. `undefined`/absent when none are set.
   * `override_count` (used by `tenantPlanLabel`) stays the authority for "is
   * this tenant Custom"; this is the richer view a modal needs to render
   * "linked" vs "overwritten" per axis without a second round trip.
   */
  overrides?: Partial<Record<'free' | 'extra' | 'grace' | 'storage' | 'concurrency',
    { state: string; value: number | null }>>
  /** Raw `billing_subscriptions.status`, or null when there is no live one. */
  subscriptionStatus: string | null
  /**
   * The badge state, derived SERVER-SIDE so its precedence lives in one place.
   * `disputed` outranks `past_due` outranks `canceling` — the order `cost-check`
   * actually enforces, since the first two both pause AI today.
   * `none` = no live subscription (free plan), which is not a problem to fix.
   */
  health: 'none' | 'active' | 'incomplete' | 'past_due' | 'disputed' | 'canceling'
  /** Cached billable seats. Compare against `memberCount` — a mismatch is a
   *  silent revenue leak one way and an overcharge the other. */
  seatCount: number | null
  graceUntil: string | null
  currentPeriodEnd: string | null
  /** Raw membership headcount (not billable seats). */
  memberCount: number
  /**
   * This tenant's identity-only mode override (0175). `null` = inherit the
   * deployment default, which is the state of every tenant.
   *
   * Personal tenants only — an organization is always `'full'` and reports
   * `personalModeOverride: null`, `effectiveMode: 'full'`, `modeSource: 'default'`
   * (its TYPE decided, not a setting). Use `?type=personal` to find the rows
   * this is interesting on.
   */
  personalModeOverride?: TenantMode | null
  /** What this tenant actually IS, resolved server-side through the same
   *  function the enforcement reader uses — so the list cannot display a mode
   *  the gate would disagree with. */
  effectiveMode?: TenantMode
  /** Which link decided — so the screen can say "inherited" rather than render
   *  a blank control beside a live behaviour. */
  modeSource?: TenantModeSource
}

export interface AdminTenantsResponse {
  period: string
  limit: number
  offset: number
  total: number
  tenants: AdminTenantRow[]
}

const period = (p?: string) => (p ? `?period=${encodeURIComponent(p)}` : '')

// ── Platform Overview (redesign phase 4a) ───────────────────────────────

/**
 * One tenant on the Overview's top-spenders list.
 *
 * BILLING-CURRENCY MICRO units, denominated in `OverviewResponse.billingCurrency`
 * — what the CUSTOMER was charged, which is the same figure their caps are
 * enforced against. Not USD, and not the platform's cost of goods: that is
 * `margin.providerCostUsd`, which stays USD and is deliberately kept apart.
 */
export interface OverviewTenant {
  tenantId: string
  /** `null` when the tenant is gone — usage rows outlive tenant deletion, and
   *  their spend still belongs in the total. */
  name: string | null
  type: string | null
  spendBillingMicro: number
  llmCostBillingMicro: number
  resourceCostBillingMicro: number
  messages: number
}

export interface OverviewResponse {
  period: string
  /** Margin and dunning are meaningless with billing off — the screen says so
   *  rather than rendering zeros that read as "we made nothing". */
  billingEnabled: boolean
  /**
   * The deployment's charged currency — the unit of EVERY `*BillingMicro`
   * field below, and the symbol the screen must format them with.
   *
   * On the wire because the SPA cannot resolve it: `formatMoney` is
   * dependency-free and defaults to `'USD'`, so a payload that omitted this
   * would render an RM50 total as "$50.00". A DTO that carries billing-native
   * amounts without their unit is the bug, not the formatter.
   */
  billingCurrency: string
  totals: {
    /**
     * BOTH ledgers, in BILLING micro. This is the number that must equal the
     * sum of the rows beneath it, which is why the server sums the same array
     * it ranks.
     *
     * It is what CUSTOMERS WERE CHARGED. It is emphatically not what providers
     * billed the platform — that is `margin.providerCostUsd`, in USD, and the
     * two are compared as a RATE rather than a difference precisely because
     * they are different currencies.
     */
    spendBillingMicro: number
    llmCostBillingMicro: number
    resourceCostBillingMicro: number
    messages: number
    activeTenants: number
  }
  topTenants: OverviewTenant[]
  byModel: {
    model: string
    /** Billing micro, like the headline this table breaks down. */
    costBillingMicro: number
    inputTokens: number
    outputTokens: number
    messages: number
  }[]
  grace: {
    /**
     * Spend the PLATFORM absorbed this period — money given away.
     *
     * BILLING-CURRENCY MICRO, like every other spend figure on this screen and
     * like the ceiling it is measured against. It was USD while the ceiling's
     * fallback chain was (an env var and a shipped $5 constant); Gate 1 settled
     * the whole rung in one unit, because grace is the LAST step of the funding
     * waterfall and a waterfall measured in two units is not one.
     */
    absorbedBillingMicro: number
    tenants: number
    /** Per tenant, per period, same unit. Exposure is this × every tenant. */
    capBillingMicroPerTenant: number
  }
  margin: {
    /** Minor units of THIS currency. Revenue is the one figure on the wire
     *  that is not USD microdollars: it is what the customer was actually
     *  charged, and converting it would need an FX rate this system does not
     *  have (the display rate is explicitly not one). */
    currency: string
    creditRateMinorPerUsd: number
    revenueMinor: number
    topupRevenueMinor: number
    subscriptionRevenueMinor: number
    // `creditSoldUsd` is GONE (P8/P9). It was `SUM(amount_usd_micro)` — the
    // dollars a customer's payment bought — and a face-value top-up has no such
    // quantity: RM50 buys RM50, so `topupRevenueMinor` above already says it.
    // The field outlived its writer here, which is how a client type comes to
    // promise a number the server never sends: `undefined` where a `number` was
    // declared, and TypeScript none the wiser.
    /** What the PROVIDER billed us — USD microdollars, cost of goods. */
    providerCostUsd: number
    /** What CUSTOMERS were charged, in `margin.currency` micro units. Compared
     *  to `providerCostUsd` as a RATE, never a difference: they are different
     *  currencies and this system has no FX rate to bridge them. */
    chargedBillingMicro: number
    /** Minor units collected per USD of provider cost — directly comparable to
     *  `creditRateMinorPerUsd`, the number the operator SET. `null` when
     *  nothing was spent; a zero denominator is not infinite margin. */
    realisedRateMinorPerUsd: number | null
    /** Paid invoices in another currency, excluded rather than mis-summed. */
    excludedInvoices: number
  }
  alerts: {
    pastDue: number
    disputed: number
    inGrace: number
    /** `null` = the count could not be read (billing off, or the query failed).
     *  NOT the same as 0, and must not render as "nothing stuck". */
    stuckWebhooks: number | null
    overCap: number
  }
}

// ── Platform settings (redesign phase 5) ────────────────────────────────

/**
 * The deployment's own behaviour config, plus the environment-held values that
 * are deliberately NOT editable.
 *
 * `settings` is what is STORED (`null` = no override); `resolved` is what is
 * actually IN FORCE. Both are on the wire because a `null` override is the
 * common case, and an empty input beside a live ceiling tells the operator
 * nothing about which number the enforcer is using.
 */
export interface PlatformSettingsStored {
  /** `null` = inherit the environment default. `0` = grace OFF globally.
   *  These are different states and both are legitimate. */
  graceCapBillingMicro: number | null
  /** `null` = the shipped default (2). `0` = self-service org creation off,
   *  every organization provisioned from this console. */
  maxFreeOrgsPerUser: number | null
  /** `null` = inherit `brand.yaml`'s `experience.landing`. Not a missing value:
   *  it is the state of every deployment until an operator chooses, and the
   *  control has to be able to return TO it. */
  landingExperience: LandingExperience | null
  /** Where plans come from (0170). `null` = unchosen = `'provider'`, i.e. the
   *  behaviour that shipped before the column existed. */
  planAuthority: PlanAuthority | null
  /** The non-payment fallback plan (0163). `null` = nothing configured, and
   *  unlike its siblings there is NO fallback chain — applying it is refused
   *  in words rather than guessing a plan. */
  manualFallbackPlanId: string | null
  /** The preset a new customer is seeded from (0165). `null` = nothing
   *  configured, and provisioning says so rather than silently using free. */
  defaultPlanId: string | null
  /**
   * Which cost the AI assistant's chat window shows, deployment-wide (0174).
   *
   * `null` = unchosen, and unlike every sibling here that does NOT resolve to a
   * single value: the platform-admin org shows operator cost and every other
   * tenant shows customer cost, then the answer is CLAMPED per viewer. That is
   * why there is no `resolved.chatCostVisibility` beside it — one number would
   * be wrong on every screen but one.
   */
  chatCostVisibility: ChatCostVisibility | null
  /** What a personal tenant is by default on this deployment (0175). `null` =
   *  not chosen, which resolves to `'full'` — the behaviour that shipped before
   *  the column. `'identity-only'` is a real setting and must never be
   *  collapsed with `null`; it is the whole feature. */
  defaultPersonalTenantMode: TenantMode | null
  /** Whether the keybook exists on this deployment (0179). `null` = not
   *  chosen → available, the shipped behaviour. `false` disables the keybook
   *  API, the cred_to_env agent tool, and hides the page. */
  keybookEnabled: boolean | null
  /** The deployment-wide version-retention default (#452). `null` = the
   *  shipped default (50). An integer 1..500 when set — how many superseded
   *  versions of a file are kept before a prune removes the rest. A per-tenant
   *  ceiling (Settings → Storage) overrides this for that tenant only. */
  defaultVersionRetention: number | null
  /**
   * Whether the disk-alarm webhook is armed (0200) — PRESENCE ONLY. The URL is
   * a bearer capability and never crosses to the browser, so this is a boolean,
   * not the value: the editor shows "Configured / Not configured" and can
   * replace or clear it, but never display or pre-fill it.
   *
   * OPTIONAL because a pre-0200 API instance omits it during the rolling restart
   * that introduces this feature — the same rolling-restart hazard `LandingEditor`
   * guards below. Read it as `=== true`, never as a bare boolean: `undefined`
   * (mid-deploy) must not be asserted as "not configured".
   */
  diskAlarmWebhookConfigured?: boolean
  /** The disk alarm's email recipients (0203, #574), comma-separated, returned
   *  RAW, unlike the webhook beside it, this is not a secret. `null` = not
   *  configured, which the console renders as the PLATFORM_ADMIN_EMAILS
   *  fallback rather than as an empty list. OPTIONAL: an API predating 0203
   *  omits it during a rolling restart. */
  diskAlarmEmails?: string | null
}

/** What is in force, alongside which link of each chain answered. */
export interface PlatformSettingsResolved {
  graceCapBillingMicro: number
  /** Which link of `setting → env → default` answered. */
  graceCapSource: 'setting' | 'env' | 'default'
  maxFreeOrgsPerUser: number
  landingExperience: LandingExperience
  /** `setting → brand` — only two links, because there is no env layer here. */
  landingSource: LandingSource
  planAuthority: PlanAuthority
  /**
   * Whether plans APPLY on this deployment — the combination of the authority
   * above and `BILLING_ENABLED`, resolved server-side.
   *
   * Not re-derivable here and deliberately not re-derived: it is neither
   * `planAuthority === 'manual'` nor `billingEnabled`, and a screen that
   * guessed would show a plan panel whose limits are not being enforced.
   */
  plansAreMeaningful: boolean
  /**
   * The deployment-wide personal-tenant mode (0175), resolved server-side by
   * the same function the enforcement reader uses — with NO tenant override
   * applied, which is what makes it a statement a deployment-wide screen may
   * make. A tenant carrying its own override resolves differently, and that
   * belongs to the tenant's own row.
   */
  defaultPersonalTenantMode: TenantMode
  /** `setting → default` — two links, and its own union rather than the
   *  per-tenant one: there is no tenant in play, so `'tenant'` is not a
   *  possible answer here and the type must not offer it. */
  defaultPersonalTenantModeSource: PlatformModeSource
  /** `setting → true`, resolved by the same function the /api/keybook gate and
   *  the cred_to_env tool read (0179). */
  keybookEnabled: boolean
  /** `setting → shipped default (50)`, resolved by `resolveVersionRetention`
   *  with no tenant ceiling in play — the same deployment-wide-only stance
   *  `defaultPersonalTenantMode` above takes (#452). */
  defaultVersionRetention: number
}

/**
 * Non-blocking context for the landing choice (§1.5 of the runtime-landing
 * plan). Zero curated tenants + a `launcher` landing means every user lands on
 * an empty grid that reads as a launcher which failed to load — worth saying,
 * never worth refusing the save over: an operator legitimately sets the landing
 * first and curates after.
 */
export interface PlatformAdvisory {
  tenantsWithShortcuts: number
  /**
   * How many personal tenants a change to `defaultPersonalTenantMode` would
   * actually reach — those with no override of their own (0175).
   *
   * The confirmation must be CONCRETE. "This affects every personal tenant
   * whose override is unset" is a sentence an operator cannot size, and what is
   * being sized is a Drive going out of reach for that many people.
   */
  personalTenantsInheritingMode: number
  /** The complement: tenants carrying an explicit override, which the change
   *  will NOT reach. Named so the dialog can say why the two numbers differ
   *  rather than leaving the gap to be inferred. */
  personalTenantsWithModeOverride: number
}

export interface PlatformSettingsResponse {
  settings: PlatformSettingsStored
  /** Every plan, so the fallback picker has names without a second round trip.
   *  `freezesUploads` is the plan's `canCreateContent === false`, resolved by the
   *  same predicate the gate enforces. */
  plans: { id: string; name: string; isActive: boolean; freezesUploads: boolean }[]
  resolved: PlatformSettingsResolved
  environment: {
    billingEnabled: boolean
    /** COUNT only, never the addresses — the allowlist is the recovery path
     *  for a membership mistake, and printing it would turn "who can I
     *  escalate through" into a one-screen answer. */
    platformAdminEmailCount: number
    /** What the server resolved from BRAND_KEY, and how. 'unset' = no key,
     *  serving the compiled default; 'failed' = a key was set and did not
     *  resolve (the server booted UNBRANDED with a [brand] FAILED banner). */
    brandKey: string
    brandResolution: 'configured' | 'unset' | 'failed'
    /** The loader's message when `brandResolution === 'failed'`, else null. */
    brandFailure: string | null
    appOrigin: string | null
    graceCapEnv: string | null
    /** What clearing the landing override falls back TO. The editor offers
     *  "use the build default" as a third state and has to name which surface
     *  that is — the phrase alone is a question, not an answer. */
    brandLanding: LandingExperience
  }
  advisory: PlatformAdvisory
}

/**
 * The PUT's reply, which is deliberately NOT the GET's.
 *
 * It omits `environment` (nothing there can change) and `graceCapSource` (the
 * write path does not recompute it). Typing it as
 * `Pick<PlatformSettingsResponse, 'settings' | 'resolved'>` was convenient and
 * wrong: it promised a `graceCapSource` the handler has never sent, so any
 * caller reading it would have got `undefined` with the compiler's blessing.
 */
export interface PlatformSettingsWriteResponse {
  settings: PlatformSettingsStored
  resolved: Omit<PlatformSettingsResolved, 'graceCapSource'>
  advisory: PlatformAdvisory
}

/** Someone asked into a customer's org who has not arrived yet. */
export interface PendingInvite {
  id: string
  email: string
  role: string
  createdAt: string
  expiresAt: string | null
}

/** What `POST /api/admin/tenants` reports back. `owner` is the D3 branch that
 *  was taken, and it is the thing the operator has to be told. */
export interface CreateTenantResponse {
  tenantId: string
  name: string
  ownerEmail: string
  owner: 'seated' | 'invited'
  inviteEmailSent?: boolean
  /**
   * The preset the tenant was seeded from, by NAME (phase 1b).
   *
   * Absent means it fell back to the free plan — either no default preset is
   * configured or the configured one is missing/retired, both of which arrive as
   * a `warning` rather than being inferred from this field's absence.
   */
  seededFrom?: string
  /** Everything landed except this — the tenant exists. */
  warning?: string
}

/** One costly agent session. `models` is every model the session used — the
 *  old query grouped BY model, so a session that switched models appeared
 *  twice, each row understating its real cost. */
export interface ExpensiveSession {
  sessionId: string
  /** The WORKSPACE the session ran in — the row's identity and its link. */
  tenantId: string
  tenantName: string | null
  /**
   * Whose money, when that is NOT the workspace (0180) — a platform-funded
   * support session. `null` for the ordinary case, so the row carries the
   * distinction only where there is one.
   *
   * The COST above does NOT group on this: the row is the session, summed
   * across every wallet, because "which session burned the money and where" is
   * what this screen is for. Overview and the customers list group on the
   * payer instead, so a mixed session's money splits between two tenants
   * there — both right for their own question, and not meant to reconcile
   * row-for-row.
   */
  payerTenantId?: string | null
  userId: string | null
  email: string | null
  models: string[]
  /** Billing-currency MICRO — the aggregate is `SUM(cost_billing_micro)`. */
  costBillingMicro: number
  inputTokens: number
  outputTokens: number
  messages: number
  startedAt: string
  endedAt: string
  durationMin: number
}

/** One tenant's at-rest drive footprint (#64 Task 4c.1 / U1). */
export interface TopStorageTenant {
  tenantId: string
  /** `null` only if the row's tenant has since been deleted. */
  name: string | null
  totalBytes: number
  liveBytes: number
  binBytes: number
  versionsBytes: number
  quarantineBytes: number
  /** Session-private attachments (0218, #639) — already summed into
   *  `totalBytes` (unlike `scratchBytes`); projected here as its own
   *  component so the shown breakdown sums to the total. */
  attachmentsBytes: number
  /** Inline chat attachments (0299, #758) — also summed into `totalBytes`,
   *  projected separately for the same reason. */
  inlineBytes: number
  /** Engine "scratch" (app-db) bytes — ride along separately, NOT counted
   *  against the tenant's quota (#535). */
  scratchBytes: number
  computedAt: string | null
}
export interface TopStorageResponse {
  tenants: TopStorageTenant[]
}

/** One recorded governance change. Shape mirrors the `budget_audit` row. */
export interface AuditEntry {
  id: string
  /** `null` for platform-scope actions (catalog, pricing, admin grants). */
  tenantId: string | null
  actorUserId: string | null
  actorEmail: string | null
  action: string
  /** What the action targeted: role name, team id, member email, feature… */
  subject: string | null
  /** As captured. An explicit `null` means "set to null" (e.g. cleared to
   *  unlimited) and is NOT the same as the field being absent from the action. */
  oldValue: unknown
  newValue: unknown
  createdAt: string
}

export interface AuditPage {
  entries: AuditEntry[]
  total: number
  limit: number
  offset: number
}

export interface AuditQuery {
  limit?: number
  offset?: number
  action?: string
  /** Exact actor email, case-insensitive. */
  actor?: string
  /** ISO date strings; `since` inclusive, `until` exclusive. */
  since?: string
  until?: string
}

function auditQs(opts: AuditQuery): string {
  const q = new URLSearchParams()
  if (opts.limit != null) q.set('limit', String(opts.limit))
  if (opts.offset != null) q.set('offset', String(opts.offset))
  if (opts.action) q.set('action', opts.action)
  if (opts.actor?.trim()) q.set('actor', opts.actor.trim())
  if (opts.since) q.set('since', opts.since)
  if (opts.until) q.set('until', opts.until)
  const s = q.toString()
  return s ? `?${s}` : ''
}

/** One host's storage volume as the worker sweep last measured it (#450). */
export interface DiskHostSample {
  host: string
  path: string
  totalBytes: number
  freeBytes: number
  /** The sweep's own DECISION, never recomputed on the client. */
  level: 'ok' | 'warn' | 'page'
  trend: 'unknown' | 'steady' | 'shrinking'
  ttfMs: number | null
  observedAt: string
}

export interface DiskPressureResponse {
  hosts: DiskHostSample[]
  markerCensus: { totalBytes: number; observedAt: string } | null
  lastBlobGc: { deleted: number; reclaimedBytes: number | null; observedAt: string } | null
  /**
   * Who grew. Queried live from the same helper — and with the same limit — the
   * alarm email uses, so the page and the panel cannot name a different set OR
   * a different NUMBER of tenants for one volume.
   *
   * OPTIONAL, and that is not defensive padding: the SPA ships from Cloudflare
   * Pages independently of the API, so during a rolling restart a new panel can
   * be talking to an instance that predates this field. Typed as present, the
   * panel's `.length` would throw on `undefined` and blank the whole thing —
   * the same reason `mode` on `my-tenants` is optional with a stated default.
   */
  topTenants?: Array<{ tenantId: string; bytes: number }>
  /**
   * Which legs FAILED, named rather than folded into their value.
   *
   * Every leg's empty/null already means something the panel says out loud —
   * "no host is reporting", "the GC has never run, that is likely the cause" —
   * and each of those is false if the query merely failed. Absent on an API
   * predating this field, which is why readers must default it.
   */
  unavailable?: string[]
}

export const budgetApi = {
  // ─── Reads ────────────────────────────────────────────────────────

  /** Tier-3. Server-scoped to the caller; there is no email parameter by
   *  design, so this cannot be pointed at another member. */
  async getMyUsage(tenantId: string, p?: string) {
    return request<MyUsageResponse>(`/api/orgs/${tenantId}/usage/me${period(p)}`)
  },

  /** Tier-2. Requires `manage_budget:read`. */
  async getOrgUsage(tenantId: string, p?: string) {
    return request<OrgUsageResponse>(`/api/orgs/${tenantId}/usage${period(p)}`)
  },

  /** Tier-1. Paginated — never fetch "all tenants" unbounded. `type`/`search`
   *  narrow the set (every user has a personal tenant, so the unfiltered list
   *  is mostly personal noise); omit both for every tenant. */
  async listTenants(
    opts: {
      limit?: number
      offset?: number
      period?: string
      type?: 'organization' | 'personal'
      search?: string
      /** Exact tenant id — the customer detail page's fetch. */
      id?: string
      /** Filter to tenants explicitly on this plan (by the override pointer —
       *  see the route's documented limitation). */
      planId?: string
    } = {},
  ) {
    const q = new URLSearchParams()
    if (opts.limit != null) q.set('limit', String(opts.limit))
    if (opts.offset != null) q.set('offset', String(opts.offset))
    if (opts.period) q.set('period', opts.period)
    if (opts.type) q.set('type', opts.type)
    if (opts.search?.trim()) q.set('q', opts.search.trim())
    if (opts.id?.trim()) q.set('id', opts.id.trim())
    if (opts.planId?.trim()) q.set('planId', opts.planId.trim())
    const qs = q.toString()
    return request<AdminTenantsResponse>(`/api/admin/tenants${qs ? `?${qs}` : ''}`)
  },

  /**
   * One tenant, in exactly the shape the list renders.
   *
   * Deliberately the SAME endpoint rather than a sibling: the plan, health,
   * currency chain and spend aggregation are all derived server-side, and a
   * second single-tenant route would be a second place for them to drift. A
   * console whose list and detail page disagree about a customer is worse than
   * one that only has a list.
   */
  async getTenant(tenantId: string, period?: string): Promise<AdminTenantRow | null> {
    const res = await this.listTenants({ id: tenantId, limit: 1, period })
    return res.tenants[0] ?? null
  },

  /** Every email of a PERSONAL tenant's account: the main one first, then its
   *  verified secondaries. Fetched on demand ("Show all emails"). */
  async listTenantEmails(tenantId: string) {
    return request<{ emails: Array<{ email: string; primary: boolean; verified: boolean }> }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/emails`,
    )
  },

  // ─── Org-admin writes (manage_budget:write) ───────────────────────

  /** Pass `null` for unlimited. `capBillingMicro` is the single per-member spend
   *  adjustment (free/extra split removed). */
  async setMemberBudget(
    tenantId: string,
    email: string,
    caps: { capBillingMicro?: BudgetCap },
  ) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/members/budget`, {
      method: 'PATCH',
      body: JSON.stringify({ email, ...caps }),
    })
  },

  async setTeamBudget(
    tenantId: string,
    teamId: string,
    caps: { capBillingMicro?: BudgetCap },
  ) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/teams/${teamId}/budget`, {
      method: 'PATCH',
      body: JSON.stringify(caps),
    })
  },

  /** Omitting `capBillingMicro` deletes the role override so the role falls back to
   *  its hardcoded default. */
  async setRoleBudget(
    tenantId: string,
    role: string,
    caps: { capBillingMicro?: BudgetCap },
  ) {
    return request<{ ok: boolean; reset?: boolean }>(`/api/orgs/${tenantId}/roles/budget`, {
      method: 'PATCH',
      body: JSON.stringify({ role, ...caps }),
    })
  },

  /** `pct` are integers in (0,100]; `absUsd` are microdollars. Both ≤10
   *  entries. An empty `pct` array means "no percentage alerts" and is
   *  NOT the same as omitting it (which restores the defaults). */
  async setAlertThresholds(
    tenantId: string,
    thresholds: { pct?: number[]; absUsd?: number[] } | null,
  ) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/budget-alert-thresholds`, {
      method: 'PUT',
      body: JSON.stringify(thresholds ?? {}),
    })
  },

  /** Softens ONLY org-internal caps. The platform-set tenant ceiling stays
   *  hard regardless — the UI must say so, or "warn only" reads as
   *  "nothing stops me" and the eventual 402 is a surprise. */
  async setEnforcementMode(tenantId: string, mode: 'enforce' | 'warn_only') {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/enforcement-mode`, {
      method: 'PUT',
      body: JSON.stringify({ mode }),
    })
  },

  // ─── Platform-admin writes (requirePlatformAdmin) ─────────────────

  /**
   * Enforcement mode for one tenant, from the PLATFORM console.
   *
   * Deliberately not `setEnforcementMode` above: that hits the org router, whose
   * gate needs membership in the target tenant plus `manage_budget:write`. A
   * platform admin has neither for someone else's customer, so this is the
   * platform-gated sibling (`customers:write`) writing through the same audited
   * setter.
   */
  async setTenantEnforcementMode(tenantId: string, mode: 'enforce' | 'warn_only') {
    return request<{ success: boolean; tenantId: string; mode: string }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/enforcement-mode`,
      { method: 'PUT', body: JSON.stringify({ mode }) },
    )
  },

  /**
   * Set / clear authoritative overrides on a customer.
   *
   * ── UNAMBIGUOUS TAGGED SHAPE (0177 F2 round) ─────────────────────────
   *
   * The old body (`{ freeCapBillingMicro: number | null, … }`) collapsed
   * three states — omitted, cleared, and unlimited/disabled — onto two
   * spellings, and could not clear an axis back to plan authority at all.
   *
   * Every axis is now a discriminated `TenantOverrideState`:
   *
   *   - omitted           → leave unchanged
   *   - `inherit`         → DELETE the override; the projector reads the plan
   *   - `finite`          → set to a specific value
   *   - `unlimited`       → free only, explicit no-ceiling row
   *   - `disabled`        → extra only, explicit no-overage row
   *
   * Blank/clear in a UI editor MUST map to `{ state: 'inherit' }` — not to a
   * value spelling like unlimited or disabled, and not to omission.
   */
  async setTenantOverrides(
    tenantId: string,
    overrides: {
      free?: TenantFreeCapOverride
      extra?: TenantExtraCapOverride
      concurrency?: TenantConcurrencyOverride
      storage?: TenantStorageOverride
      grace?: TenantGraceCapOverride
    },
  ) {
    return request<{ ok: true; applied: EffectiveCeilings }>(
      `/api/admin/tenants/${tenantId}/caps`,
      { method: 'PUT', body: JSON.stringify({ overrides }) },
    )
  },

  async getFeaturePricing() {
    return request<{ pricing: FeaturePricingRow[] }>('/api/admin/feature-pricing')
  },

  /** Omit a field to leave it alone. `chargeable: false` = metered but
   *  free — usage still records at cost 0, it does not stop recording. */
  async setFeaturePricing(
    feature: string,
    values: { chargeable?: boolean; unitRateUsd?: number; unit?: string },
  ) {
    return request<{ success: boolean; pricing: FeaturePricingRow }>('/api/admin/feature-pricing', {
      method: 'PUT',
      body: JSON.stringify({ feature, ...values }),
    })
  },

  /**
   * Restrict this tenant to a set of models, or `null` to lift the restriction.
   *
   * The PLATFORM layer: excluded models vanish from the tenant's picker. An
   * empty array is normalized to `null` server-side — both mean unrestricted,
   * and an admin who means "block everything" wants a budget cap, not a model
   * list that would brick every agent turn.
   */
  async setAllowedModels(tenantId: string, allowedModels: string[] | null) {
    return request<{ success: boolean; tenantId: string; allowedModels: string[] | null }>(
      `/api/admin/tenants/${tenantId}/allowed-models`,
      { method: 'PUT', body: JSON.stringify({ allowedModels }) },
    )
  },

  /**
   * The currency this tenant's users see money in, and the multiplier used to
   * get there.
   *
   * DISPLAY ONLY — canonical amounts stay USD microdollars and `rate` is a
   * multiplier, not an FX quote, which is why every converted figure carries
   * the `≈` marker. `USD` is only accepted at rate 1 (or null).
   *
   * `rate: null` CLEARS the override so the tenant derives its rate from
   * `billing_settings.credit_rate_minor_per_usd` — the default, and the state
   * that can never drift from what customers actually pay. Pass a number only
   * to pin a rate deliberately.
   */
  async setDisplayCurrency(
    tenantId: string, currency: string, rate: number | null,
    /** ISO-8601. Omitted = now. The past is refused by the route AND a trigger:
     *  a backdated epoch silently restates this customer's history. */
    effectiveFrom?: string,
  ) {
    return request<{
      success: boolean; tenantId: string; currency: string; rate: number | null
      /** In force now. `false` = scheduled; absent (an older API) = read as applied. */
      applied?: boolean
    }>(
      `/api/admin/tenants/${tenantId}/display-currency`,
      { method: 'PUT', body: JSON.stringify({ currency, rate, ...(effectiveFrom ? { effectiveFrom } : {}) }) },
    )
  },

  /**
   * This tenant's override for which cost its chat window shows (0174).
   *
   * `null` CLEARS it so the tenant follows `platform_settings` again — a real
   * operator action, not an absence, and the reason the parameter is not
   * optional: an omitted value and an explicit "inherit" would be the same call.
   *
   * Gated `settings:write`, NOT `customers:write` like its neighbours on the
   * Configuration tab. What it decides is who may see the PLATFORM's cost of
   * goods, which is the same subject as the deployment-wide setting rather than
   * a fact about this customer — see the route's docblock.
   */
  async setChatCostVisibility(tenantId: string, visibility: ChatCostVisibility | null) {
    return request<{ success: boolean; tenantId: string; chatCostVisibility: ChatCostVisibility | null }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/chat-cost-visibility`,
      { method: 'PUT', body: JSON.stringify({ chatCostVisibility: visibility }) },
    )
  },

  /**
   * This customer's role-default permission overrides (`customers:read`).
   *
   * TEMPORARY — permission defaults are platform-administered for now; see
   * TODO(permissions-relocation) at the routes. The org-side
   * `/api/orgs/:id/capability-grants` pair this replaces was deleted, not
   * re-gated.
   *
   * An absent pair means "the built-in default", never "denied" — the response
   * carries only OVERRIDES, and the grid fills the rest from `CAPABILITIES`.
   */
  async getCapabilityGrants(tenantId: string) {
    return request<{ grants: Array<{ capability: string; role: string; level: string }> }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/capability-grants`,
    )
  },

  /**
   * Set or reset ONE role default (`customers:write`).
   *
   * `level: null` resets to the built-in default — which is why the parameter is
   * required rather than optional: an omitted level and an explicit reset would
   * otherwise be the same call, and they are different acts.
   */
  async setCapabilityGrant(tenantId: string, capability: string, role: string, level: string | null) {
    return request<{ ok: boolean; reset: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/capability-grants`,
      { method: 'PUT', body: JSON.stringify({ capability, role, level }) },
    )
  },

  /**
   * Org-layer model blocks for this tenant (`manage_budget:read`).
   *
   * A row means BLOCKED; absence means allowed. The ORG layer can only narrow
   * what the platform allows — see
   * docs/plans/2026-07-30_MODEL_ACCESS_GOVERNANCE.md.
   */
  async getModelRestrictions(tenantId: string) {
    return request<{ restrictions: ModelRestrictionRow[] }>(
      `/api/orgs/${tenantId}/model-restrictions`,
    )
  },

  /**
   * Replace ONE subject's blocked-model set. Whole-set replace, not add/remove:
   * the editor is a grid whose state IS the set. An empty array clears the
   * subject.
   */
  async setModelRestrictions(
    tenantId: string,
    subjectType: 'role' | 'team' | 'user',
    subjectId: string,
    modelIds: string[],
  ) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/model-restrictions`, {
      method: 'PUT',
      body: JSON.stringify({ subjectType, subjectId, modelIds }),
    })
  },

  /**
   * What ONE tenant is charged, off-catalogue.
   *
   * All five fields every time, `null` for the ones that do not apply — the
   * route refuses a partial body, because a seat price surviving from a
   * previous edit is how an inert price outlives the deal that removed it.
   * Amounts are MINOR units of `priceCurrency`; `0` is a real free arrangement
   * and is never the same as `null`.
   */
  async setContractPrice(tenantId: string, price: ContractPriceInput) {
    // The route also echoes the RESOLVED `amountDue`, but this deliberately
    // does not type it: the caller refetches the customer row, whose own
    // `amountDue` is the one the tile renders. A second copy of that shape here
    // is one that drifts from the tile it is supposed to agree with.
    return request<{ ok: boolean; tenantId: string }>(
      `/api/admin/tenants/${tenantId}/contract-price`,
      { method: 'PUT', body: JSON.stringify(price) },
    )
  },

  async setStorageQuota(tenantId: string, storageQuotaBytes: number) {
    return request<{ ok: boolean }>(`/api/admin/tenants/${tenantId}/storage-quota`, {
      method: 'PUT',
      body: JSON.stringify({ storageQuotaBytes }),
    })
  },

  /**
   * This tenant's own override of the write freeze's plan rule (P3, 0189).
   *
   * `canCreate: true/false` overrides the plan regardless of what it says;
   * `null` clears the override so the tenant inherits its plan's
   * `canCreateContent` again. Required, not optional — an omitted value and
   * an explicit "inherit" would otherwise be the same call.
   */
  async setTenantUploads(tenantId: string, canCreate: boolean | null) {
    return request<{ ok: boolean; tenantId: string; canCreate: boolean | null }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/uploads`,
      { method: 'PUT', body: JSON.stringify({ canCreate }) },
    )
  },

  /**
   * This tenant's own override of the plan's per-principal request-rate
   * ceilings (P3, 0189). Whole-object replace, like `setAllowedModels`'s
   * list — there is no per-tier clear.
   *
   *   `{ db: 1200 }`  — override the db tier only; sdk/scripts keep
   *                      inheriting the plan
   *   `null`          — clear the WHOLE override, every tier inherits again
   *
   * The route refuses a value at or below the tier's platform default (it
   * would be inert — the limiter clamps it back up) and above the safety-valve
   * ceiling, mirroring the plan catalog's own rate-limit write.
   */
  async setTenantRateLimits(
    tenantId: string,
    limits: Partial<Record<PlanRateLimitTier, number>> | null,
  ) {
    return request<{ ok: boolean; tenantId: string; limits: Partial<Record<PlanRateLimitTier, number>> | null }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/rate-limits`,
      { method: 'PUT', body: JSON.stringify({ limits }) },
    )
  },

  /**
   * This customer's seat ceiling. `null` CLEARS the override so the tenant
   * inherits its plan's `max_seats`; a number sets it, and `0` is a real
   * ceiling meaning nobody may be seated.
   *
   * Its own endpoint rather than an axis on `setTenantOverrides`: that one
   * writes the authoritative override table which `applyEntitlements` then
   * PROJECTS, and the seat ceiling is deliberately resolved at read time
   * instead (Doc A §3.2 — no seeding, no propagation).
   */
  async setSeatCap(tenantId: string, maxSeats: number | null) {
    // NO `effectiveMaxSeats`: the route deliberately does not return one,
    // because `/api/admin/*` runs scoped and `resolveSeatCap` fails open to
    // `null` there — it would be a field that always said "unlimited". Leaving
    // it on the type is worse than omitting it: a reader gets `undefined` with
    // no type error, and on THIS axis undefined collapses into exactly that
    // wrong "unlimited".
    return request<{
      ok: boolean; changed: boolean
      maxSeatsOverride: number | null
    }>(`/api/admin/tenants/${tenantId}/seat-cap`, {
      method: 'PUT',
      body: JSON.stringify({ maxSeats }),
    })
  },

  /**
   * Set this tenant's per-tenant included-seats override (0180) — `null` to
   * inherit the plan. Under manual billing this is ALSO the tenant's seat
   * ceiling, so it is the operator's manual seat grant. Its own endpoint for the
   * same reason as `setSeatCap`: resolved at read time, never projected.
   */
  async setIncludedSeats(tenantId: string, includedSeats: number | null) {
    return request<{
      ok: boolean; changed: boolean
      includedSeats: number | null
    }>(`/api/admin/tenants/${tenantId}/included-seats`, {
      method: 'PUT',
      body: JSON.stringify({ includedSeats }),
    })
  },

  /** Resolved per-tenant pricing: the full tenant → plan → global chain, with
   *  the tenant's effective plan named so the editor can attribute an
   *  inherited rate to the layer it actually comes from. */
  async getTenantFeaturePricing(tenantId: string) {
    return request<{
      tenantId: string
      tenantName: string
      planId: string
      planName: string
      pricing: TenantFeaturePricingRow[]
    }>(`/api/admin/tenants/${tenantId}/feature-pricing`)
  },

  /** Upsert one per-tenant override. Omit a field to leave it alone.
   *  `chargeable`/`baseRateUsd` null = inherit the global default. */
  async setTenantFeaturePricing(
    tenantId: string,
    feature: string,
    values: {
      mode?: 'flat' | 'tiered'
      chargeable?: boolean | null
      baseRateUsd?: number | null
      tiers?: PricingTier[]
    },
  ) {
    return request<{ success: boolean; override: TenantPricingOverride }>(
      `/api/admin/tenants/${tenantId}/feature-pricing`,
      { method: 'PUT', body: JSON.stringify({ feature, ...values }) },
    )
  },

  /** Remove a tenant's override, reverting the feature to the global default. */
  async clearTenantFeaturePricing(tenantId: string, feature: string) {
    return request<{ success: boolean; removed: boolean }>(
      `/api/admin/tenants/${tenantId}/feature-pricing/${feature}`,
      { method: 'DELETE' },
    )
  },

  // ── Per-plan pricing (the middle layer: tenant → plan → global) ──

  /** The whole features × plans matrix in one read. Use this for the grid —
   *  calling `getPlanFeaturePricing` per plan is an N+1 that grows with the
   *  catalog. `overrides` carries only the (plan, feature) pairs that actually
   *  have a row; everything absent inherits. */
  async getPricingMatrix() {
    return request<{
      plans: { id: string; name: string; sortOrder: number }[]
      globals: FeaturePricingRow[]
      overrides: (TenantPricingOverride & { planId: string })[]
    }>('/api/admin/plan-feature-pricing')
  },

  /** Resolved per-plan pricing (global default + this plan's overrides). */
  async getPlanFeaturePricing(planId: string) {
    return request<{ planId: string; planName: string; pricing: TenantFeaturePricingRow[] }>(
      `/api/admin/plans/${planId}/feature-pricing`,
    )
  },

  /** Upsert one per-plan override. Same partial-save contract as the tenant one. */
  async setPlanFeaturePricing(
    planId: string,
    feature: string,
    values: {
      mode?: 'flat' | 'tiered'
      chargeable?: boolean | null
      baseRateUsd?: number | null
      tiers?: PricingTier[]
    },
  ) {
    return request<{ success: boolean; override: TenantPricingOverride }>(
      `/api/admin/plans/${planId}/feature-pricing`,
      { method: 'PUT', body: JSON.stringify({ feature, ...values }) },
    )
  },

  /**
   * Tier-1 view of ONE tenant's per-member usage.
   *
   * Same response shape as `getOrgUsage` because it is the same aggregation —
   * `buildOrgUsage`, shared so the platform console and the org's own dashboard
   * can never disagree about a member's spend. The GATES differ (platform
   * capability vs org membership + capability), which is the whole reason the
   * maths was extracted instead of the route being widened.
   */
  async getTenantUsage(tenantId: string, p?: string) {
    return request<OrgUsageResponse>(`/api/admin/tenants/${tenantId}/usage${period(p)}`)
  },

  /** Live invites only — the people this customer is still waiting on. */
  async getTenantInvites(tenantId: string) {
    return request<{ invites: PendingInvite[] }>(`/api/admin/tenants/${tenantId}/invites`)
  },

  /**
   * Resend / revoke a pending invite from the PLATFORM console (5c).
   *
   * Not the org router's equivalents: those need membership in the target tenant
   * plus `manage_invite:write`, which a platform admin has for nobody else's
   * customer. Provisioning a tenant from this console CREATES the pending invite,
   * so being unable to act on it afterwards was a gap of the console's own making.
   *
   * There is deliberately no "copy link" — the invite URL embeds a bearer token
   * for joining the org, and an admin read that returned it would put a live
   * credential in a response body and a screenshot. Resend puts the link where it
   * already goes.
   */
  async resendTenantInvite(tenantId: string, inviteId: string) {
    return request<{ success: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/invites/${encodeURIComponent(inviteId)}/resend`,
      { method: 'POST' },
    )
  },

  /** Change a PENDING invite's role before it is accepted (console, `customers:write`). */
  async setTenantInviteRole(tenantId: string, inviteId: string, role: string) {
    return request<{ ok: boolean; role: string }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/invites/${encodeURIComponent(inviteId)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    )
  },

  /**
   * Grant or revoke a platform support seat. Platform console only
   * (`customers:write`); refused with NOT_PLATFORM_ADMIN if the target is not
   * a platform administrator.
   */
  async setTenantMemberPlatformSeat(tenantId: string, email: string, enabled: boolean) {
    return request<{ ok: boolean; enabled: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(email)}/platform-seat`,
      { method: 'PUT', body: JSON.stringify({ enabled }) },
    )
  },

  /**
   * Invite a BILLABLE member into a customer tenant, from the platform console.
   * The org router's invite needs membership in the target org; this one is
   * platform-gated. A platform admin inviting themselves uses this too — it is
   * an ordinary member invite pointed at their own address.
   */
  async inviteTenantMember(tenantId: string, input: { email: string; role?: string; sendEmail?: boolean }) {
    return request<{ ok: boolean; inviteId: string }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/invite`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  /**
   * Add a NON-BILLABLE platform support seat to a customer tenant. Seats the
   * address and marks it in one step; refused with NOT_PLATFORM_ADMIN unless the
   * address confers platform admin. `seated` = a new membership was created (vs
   * an existing member being marked).
   */
  async addTenantSupportSeat(tenantId: string, input: { email: string; role?: string }) {
    return request<{ ok: boolean; seated: boolean; email: string }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/support-seat`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  /**
   * Remove a platform support member from a customer tenant (console only,
   * `customers:write`). Refused with NOT_PLATFORM_ADMIN unless the target's
   * email confers platform admin; owners and the platform-admin org are refused.
   */
  async removeTenantMember(tenantId: string, email: string) {
    return request<{ ok: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(email)}`,
      { method: 'DELETE' },
    )
  },

  /**
   * Change a platform-staff member's role in a customer tenant (console only,
   * `customers:write`). Refused with NOT_PLATFORM_ADMIN for the customer's own
   * members, and LAST_OWNER when demoting the tenant's last owner.
   */
  async setTenantMemberRole(tenantId: string, email: string, role: string) {
    return request<{ ok: boolean; role: string }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(email)}/role`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    )
  },

  async revokeTenantInvite(tenantId: string, inviteId: string) {
    return request<{ success: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/invites/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE' },
    )
  },

  /**
   * The RESOLVED per-model answer for one tenant: platform → plan → org.
   *
   * Omit `email` for the tenant-wide answer (platform + plan layers, which are
   * subject-independent). Pass one to include the org layer, which is per
   * role/team/user — so "what can this customer use" genuinely has no single
   * answer without it.
   */
  async getTenantModelAccess(tenantId: string, email?: string) {
    const qs = email ? `?email=${encodeURIComponent(email)}` : ''
    return request<{
      visible: string[]
      denied: Array<{ modelId: string; layer: 'plan' | 'org'; planId?: string; planName?: string; by?: string }>
    }>(`/api/admin/tenants/${tenantId}/model-access${qs}`)
  },

  /** Mini-apps this tenant funds for other people, and what they cost. */
  async getFundedApps(tenantId: string, p?: string) {
    return request<{
      period: string
      apps: Array<{
        id: string
        appFolderId: string
        appName: string | null
        /** `null` = no cap object; `{month: null}` = funded-unlimited;
         *  `{month: 0}` = funded but blocked. Three distinct states. */
        caps: { month?: number | null } | null
        periodCostBillingMicro: number
        createdAt: string
      }>
    }>(`/api/admin/tenants/${tenantId}/funded-apps${period(p)}`)
  },

  /** Withdraw funding (D9). Never sets a cap — that is the owner's consent. */
  async revokeAppFunding(tenantId: string, appFolderId: string) {
    return request<{ removed: boolean }>(
      `/api/admin/tenants/${tenantId}/funded-apps/${appFolderId}`,
      { method: 'DELETE' },
    )
  },

  /** #538 — this tenant's PDApps and which open without the splash. */
  async getTenantPdapps(tenantId: string) {
    return request<{
      /** The list is capped at 500 (by name). */
      truncated: boolean
      pdapps: Array<{
        appFolderId: string
        name: string
        /** Who created it, where known (the folder's audit row, else its first file version). */
        author: { name: string | null; email: string | null } | null
        /** Newest change anywhere inside the PDApp (the mark survives edits). */
        lastChangedAt: string | null
        opensWithoutSplash: { by: string; at: string } | null
      }>
    }>(`/api/admin/tenants/${tenantId}/pdapps`)
  },

  /** #538 — mark or unmark a PDApp "Opens without splash". */
  async setOpensWithoutSplash(tenantId: string, appFolderId: string, on: boolean) {
    return request<{ ok: true; opensWithoutSplash: boolean }>(
      `/api/admin/tenants/${tenantId}/pdapps/${appFolderId}/opens-without-splash`,
      { method: 'PUT', body: JSON.stringify({ on }) },
    )
  },

  // ── Audit trail (redesign phase 3) ────────────────────────────────

  /**
   * One tenant's governance history, newest first.
   *
   * Every field on a row is stored, not derived: `oldValue`/`newValue` are the
   * JSON the setter captured, and `writeBudgetAudit` normalizes an absent value
   * to an explicit `null` so "cleared to unlimited" stays distinguishable from
   * "this field was not part of the action". Rendering must preserve that —
   * collapsing both to a dash throws away the distinction the writer went out
   * of its way to keep.
   */
  async getTenantAudit(tenantId: string, opts: AuditQuery = {}) {
    return request<AuditPage>(`/api/admin/tenants/${tenantId}/audit${auditQs(opts)}`)
  },

  /** Platform-scope history: catalog, pricing, and platform-admin grants. */
  async getPlatformAudit(opts: AuditQuery = {}) {
    return request<AuditPage>(`/api/admin/audit${auditQs(opts)}`)
  },

  // ── Platform Overview + Operations (redesign phase 4) ─────────────

  /**
   * What this deployment is doing this period.
   *
   * Replaces the deleted `GET /api/admin/usage`, whose total read one ledger on
   * a rolling-day window and so could never reconcile with the per-tenant rows
   * beside it.
   */
  async getOverview(p?: string) {
    return request<OverviewResponse>(`/api/admin/overview${period(p)}`)
  },

  /** #64: tenants ranked by at-rest drive footprint, for the ops dashboard.
   *  Scratch (engine app-db) bytes ride along separately — they are NOT counted
   *  against a tenant's quota. Gated operations:read server-side. */
  async getTopStorageTenants(limit = 20) {
    return request<TopStorageResponse>(`/api/admin/storage/top-tenants?limit=${limit}`)
  },

  /** The deployment's behaviour config, resolved value included. */
  async getPlatformSettings() {
    return request<PlatformSettingsResponse>('/api/admin/platform-settings')
  },

  /**
   * Change the deployment's behaviour config.
   *
   * Pass `null` to CLEAR an override so the environment default applies again;
   * omit the key entirely to leave it alone. `0` is a real value — it switches
   * the platform's grace giveaway off globally — so a control that coerces
   * blank→0 would silently withdraw a benefit rather than restoring a default.
   */
  async setPlatformSettings(patch: {
    graceCapBillingMicro?: number | null
    maxFreeOrgsPerUser?: number | null
    planAuthority?: PlanAuthority | null
    manualFallbackPlanId?: string | null
    defaultPlanId?: string | null
    /** `null` clears the override so `brand.yaml` decides again. Never `''` —
     *  the route validates against the closed union and would 400. */
    landingExperience?: LandingExperience | null
    /** `null` leaves it unchosen, which is per-org rather than one value —
     *  see `PlatformSettingsStored.chatCostVisibility`. */
    chatCostVisibility?: ChatCostVisibility | null
    /** `null` restores the shipped default (`'full'`). Validated against the
     *  closed union server-side — no `''`, no `'identity_only'`. */
    defaultPersonalTenantMode?: TenantMode | null
    /** `false` switches the keybook off deployment-wide (0179); `null` clears
     *  the choice so the shipped "available" applies again. */
    keybookEnabled?: boolean | null
    /** An integer 1..500 (how many superseded versions to keep, deployment-wide),
     *  or `null` to inherit the shipped default (50). Bounded server-side — not
     *  `nonNegInt`, since 0 would mean "keep nothing" (#452). */
    defaultVersionRetention?: number | null
    /**
     * The disk-alarm webhook (0200) — a SECRET, WRITE-ONLY. Pass a new http(s)
     * URL to arm/replace it, or `null` to clear it. There is no read-back: the
     * value never returns from the server (only `diskAlarmWebhookConfigured`),
     * so a form cannot pre-fill it. Omit the key to leave it unchanged.
     */
    diskAlarmWebhook?: string | null
    /** Comma-separated. `null` clears it, restoring the PLATFORM_ADMIN_EMAILS
     *  fallback. A string that parses to no addresses is treated as `null` by the
     *  route rather than storing an empty list, which would silently stop email. */
    diskAlarmEmails?: string | null
  }) {
    return request<PlatformSettingsWriteResponse>(
      '/api/admin/platform-settings',
      { method: 'PUT', body: JSON.stringify(patch) },
    )
  },

  /**
   * Set ONE personal tenant's mode (0175) — the override that beats the
   * deployment-wide default above.
   *
   * `personalModeOverride` is REQUIRED, including when it is `null` (inherit). The route
   * refuses an absent key rather than reading it as a clear, so a caller that
   * forgot the field cannot silently drop an operator's override.
   *
   * Refused with `code: 'MODE_NOT_APPLICABLE_TO_ORG'` for an organization —
   * branch on the code, not the prose.
   */
  async setTenantMode(tenantId: string, personalModeOverride: TenantMode | null) {
    return request<{
      success: true
      tenantId: string
      previous: TenantMode | null
      personalModeOverride: TenantMode | null
      effectiveMode: TenantMode
      /** `false` on an identical re-submit — the write was a no-op and nothing
       *  was audited. */
      changed: boolean
    }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/mode`,
      { method: 'PUT', body: JSON.stringify({ personalModeOverride }) },
    )
  },

  /**
   * Provision a customer (phase 6, D3). Omit a cap to take the plan's grant;
   * pass `null` to make it explicitly unlimited — those are different, and the
   * route keeps them apart.
   */
  async createTenant(input: {
    name: string
    /** Optional (redesign): blank ⇒ the route seats the CALLER as owner. */
    ownerEmail?: string
    /** Optional: seed from this plan; omitted ⇒ the deployment default preset. */
    planId?: string
    storageQuotaBytes?: number
    agentConcurrencyCap?: number | null
    freeCapBillingMicro?: number | null
    extraCapBillingMicro?: number | null
    displayCurrency?: string
    displayCurrencyRate?: number | null
  }) {
    return request<CreateTenantResponse>('/api/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  /**
   * The costliest sessions this period — the leading indicator a monthly cap
   * cannot be, and the first thing you want when a customer disputes a bill.
   */
  async getExpensiveSessions(opts: { limit?: number; period?: string } = {}) {
    const q = new URLSearchParams()
    if (opts.limit != null) q.set('limit', String(opts.limit))
    if (opts.period) q.set('period', opts.period)
    const qs = q.toString()
    return request<{ period: string; limit: number; sessions: ExpensiveSession[] }>(
      `/api/admin/expensive-sessions${qs ? `?${qs}` : ''}`,
    )
  },

  /**
   * Disk pressure per host (#450).
   *
   * Reads the sample the worker sweep already stored, decision included. Neither
   * this client nor the panel it feeds measures anything — a second measurement
   * would be a second gauge to reconcile, which is the failure #450 ends.
   */
  async getDiskPressure() {
    return request<DiskPressureResponse>('/api/admin/disk-pressure')
  },

  /** Remove a plan's override, reverting the feature to the global default. */
  async clearPlanFeaturePricing(planId: string, feature: string) {
    return request<{ success: boolean; removed: boolean }>(
      `/api/admin/plans/${planId}/feature-pricing/${feature}`,
      { method: 'DELETE' },
    )
  },

  // ── Preset customisation (0164) ─────────────────────────────────────────
  /**
   * Discard every hand-made override and put the tenant back on its preset.
   *
   * Renamed from `resetToPreset`, which POSTed to a path 0177 deleted and got
   * Hono's bare `404 Not Found` back — rendered to the operator as an
   * unexplained toast. The name changed with it because the BEHAVIOUR did:
   * the old route cleared a marker and kept the values, this clears the
   * authority and re-projects the preset's ceilings over them.
   */
  returnToPlan: (tenantId: string) =>
    request<{
      ok: true
      tenantId: string
      /** Which ceiling axes had an override removed. */
      clearedAxes: string[]
      clearedFeaturePricing: number
      /** Always false — every family Custom is derived from was just emptied. */
      isCustom: false
    }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/return-to-plan`, { method: 'POST' },
    ),
  /** Name a bespoke arrangement. `null` removes the name. */
  setPlanLabel: (tenantId: string, label: string | null) =>
    request<{ ok: true; label: string | null }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/plan-label`,
      { method: 'PUT', body: JSON.stringify({ label }) },
    ),

  // ── Manual billing (0163) ───────────────────────────────────────────────

  /** The overdue queue. Reports the deployment's mode too, so an empty list can
   *  say WHY it is empty rather than claiming nobody has lapsed. */
  async getManualBillingOverdue() {
    return request<ManualBillingQueueResponse>('/api/admin/manual-billing/overdue')
  },

  /** One customer's payment state, plus what applying the fallback would do. */
  async getManualBilling(tenantId: string) {
    return request<ManualBillingResponse>(`/api/admin/tenants/${tenantId}/manual-billing`)
  },

  /**
   * Record a payment, or correct the state.
   *
   * Both fields are REQUIRED, not a sparse patch: they are always edited from one
   * form, and `paidThrough: null` (stop tracking) / `note: null` (clear it) are
   * real values that a sparse patch could not express. Include `payment` only when
   * money actually arrived — that is what makes the audit row a payment RECORD
   * rather than a date change.
   */
  async setManualBilling(tenantId: string, input: {
    paidThrough: string | null
    note: string | null
    payment?: { amountMinor: number; reference: string | null }
  }) {
    return request<{
      paidThrough: string | null
      note: string | null
      status: ManualBillingStatus
      daysOverdue: number | null
      recordedPayment: boolean
    }>(`/api/admin/tenants/${tenantId}/manual-billing`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  },

  /**
   * Move a lapsed customer onto the deployment's configured fallback plan.
   *
   * Takes no plan id ON PURPOSE — the fallback is deployment configuration, so
   * the lever on a queue full of near-identical rows cannot land the wrong plan.
   * An operator who wants a different plan uses the plan picker.
   */
  async applyManualBillingFallback(tenantId: string) {
    return request<{
      unchanged: boolean
      planId: string
      planName: string
      source?: PlanSource
      uploadsPaused: boolean
      /** Whether THIS apply took the snapshot, or found one already there. */
      snapshotted?: boolean
    }>(`/api/admin/tenants/${tenantId}/manual-billing/apply-fallback`, { method: 'POST' })
  },

  /**
   * Which customers a preset edit could reach, in two buckets (D1b, §2.5).
   *
   * `standard` = never hand-edited, ticked by default. `custom` = hand-edited at
   * least once, listed but UNTICKED: overwriting a deliberate customisation
   * takes an explicit extra click.
   *
   * The first bucket means "not hand-edited", NOT "still matches the preset" —
   * the flag does not claim that, and copy that did would be wrong for every
   * preset that has moved without being propagated.
   */
  async getPresetApplicable(planId: string) {
    return request<PresetApplicableResponse>(
      `/api/admin/plans/${encodeURIComponent(planId)}/applicable`,
    )
  },

  /**
   * Apply a preset's current values to the named customers.
   *
   * Takes explicit ids and applies at most `batchSize` per call, returning
   * `remaining` — the caller loops. Resumable rather than long-running: a
   * request that runs for minutes is one a proxy timeout turns into "half the
   * fleet updated and nobody knows which half".
   *
   * There is no undo. Every write lands an audited row, so the previous values
   * are recoverable by reading the log, but there is no bulk revert.
   */
  async applyPresetToTenants(
    planId: string,
    tenantIds: string[],
    /**
     * 'reset' (default) = "apply preset": CLEAR the four ceiling overrides and
     * re-project, returning the tenant to the preset's ceilings.
     * 'propagate' = auto-apply (F3): re-project WITHOUT clearing, so a plan value
     * change reaches inheriting tenants while an overwritten axis is preserved.
     */
    mode?: 'reset' | 'propagate',
  ) {
    return request<{
      planId: string
      applied: string[]
      failed: Array<{ tenantId: string; error: string }>
      remaining: string[]
    }>(`/api/admin/plans/${encodeURIComponent(planId)}/apply`, {
      method: 'POST',
      body: JSON.stringify(mode ? { tenantIds, mode } : { tenantIds }),
    })
  },

  /**
   * Put a paying customer back where they were — the four caps, the Custom flag,
   * and the default preset (0166, §2.9).
   *
   * **409 is a normal outcome, not a failure.** If the ceilings were changed while
   * the customer sat on the fallback, restoring would discard a deliberate act, so
   * the route refuses and NAMES the fields. The caller re-sends with
   * `force: true` to overwrite, or `discard: true` to keep today's ceilings and
   * drop the snapshot. Both are one click; neither is the default.
   */
  async restoreManualBillingFallback(
    tenantId: string,
    opts: { force?: boolean; discard?: boolean } = {},
  ) {
    return request<{
      restored: boolean
      discarded?: boolean
      reason?: 'no_snapshot' | 'edited_during_fallback' | 'baseline_unknown'
      editedKeys?: string[]
      message?: string
      planId?: string
      planName?: string
      uploadsPaused?: boolean
    }>(`/api/admin/tenants/${tenantId}/manual-billing/restore`, {
      method: 'POST',
      body: JSON.stringify(opts),
    })
  },

  // ── Manual subscription terms (Task 16, §2.1) ───────────────────────────

  /** A customer's full term history, newest first. */
  async listSubscriptionTerms(tenantId: string) {
    return request<{ terms: SubscriptionTerm[] }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription-terms`,
    )
  },

  /**
   * Append a term. Activated immediately (cache + entitlements re-run) when
   * `effectiveFrom` is today or earlier; a future date is inserted pending and
   * picked up by the sweep once it arrives.
   */
  async addSubscriptionTerm(tenantId: string, input: {
    planId: string
    /** ISO date string. Normalized to midnight UTC server-side. */
    effectiveFrom: string
    cycleCount: number
    cycleUnit: SubscriptionCycleUnit
    note?: string | null
    /** Operator-entered negotiated amount (migration 0192), billing-currency
     *  MINOR units. `null`/omitted both persist as "not recorded". */
    paymentAmountMinor?: number | null
  }) {
    return request<{ term: SubscriptionTerm }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription-terms`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  /**
   * Cancel a not-yet-effective term. 409s (`SUBSCRIPTION_TERM_ALREADY_EFFECTIVE`)
   * if the term has already taken effect — use `correctSubscriptionTerm` instead.
   */
  async deleteSubscriptionTerm(tenantId: string, termId: string) {
    return request<{ ok: true; tenantId: string; termId: string }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription-terms/${encodeURIComponent(termId)}`,
      { method: 'DELETE' },
    )
  },

  /**
   * Correct a term already on record — a mistyped date, an edited note, or a
   * plan picked in error. Every field is optional; only what's sent is
   * changed. If the corrected term is the tenant's CURRENT in-force term and
   * the plan changed, the response's `reactivated: true` says the plan cache
   * and entitlements were re-materialized as part of this call.
   */
  async correctSubscriptionTerm(tenantId: string, termId: string, patch: {
    planId?: string
    effectiveFrom?: string
    cycleCount?: number
    cycleUnit?: SubscriptionCycleUnit
    note?: string | null
    /** `null` clears the recorded amount; omit to leave it unchanged. */
    paymentAmountMinor?: number | null
  }) {
    return request<{ term: SubscriptionTerm; reactivated: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription-terms/${encodeURIComponent(termId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    )
  },

  // ── Billing tab (Task 5.4): subscription-term billing history, extra/grace
  // settlement and the deep credit ledger ─────────────────────────────────

  /**
   * The customer's full term history joined with billing status (Task 5.1) —
   * `listTermsWithBilling`'s shape, verbatim. Distinct from
   * `listSubscriptionTerms` above (the Configuration tab's positional
   * past/current/future status): the Billing tab's subscription-history
   * table needs payments, totals and the derived
   * paid/overdue/untracked/underpaid status, which that endpoint does not
   * compute. Same GET route as `listSubscriptionTerms` — the server decides
   * the response shape, not a query param.
   */
  async listSubscriptionTermsWithBilling(tenantId: string) {
    return request<{ terms: TermBillingRow[] }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription-terms`,
    )
  },

  /**
   * Record a payment against a term (Task 5.1, `manual_subscription_payments`).
   * 409s `SUBSCRIPTION_TERM_NOT_CURRENT` if `termId` is not the tenant's
   * current term — payments only ever settle the term presently in force.
   * 409s `DUPLICATE_PAYMENT_REFERENCE` if `reference` collides with a payment
   * already recorded against this term.
   */
  async recordTermPayment(tenantId: string, termId: string, input: {
    amountMinor: number
    /** ISO date string. */
    paidAt: string
    reference?: string | null
    note?: string | null
  }) {
    return request<{ payment: SubscriptionTermPayment }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription-terms/${encodeURIComponent(termId)}/payments`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  /**
   * Correct a term already on record — the Billing tab's (Task 7.1) name for
   * `correctSubscriptionTerm` above. Identical PATCH route; kept as a
   * separate export because the older Configuration-tab manual-billing view
   * and the Billing-tab redesign each import their own name for it, and
   * neither should have to know about the other's.
   */
  async updateTerm(tenantId: string, termId: string, patch: {
    planId?: string
    effectiveFrom?: string
    cycleCount?: number
    cycleUnit?: SubscriptionCycleUnit
    note?: string | null
    paymentAmountMinor?: number | null
  }) {
    return request<{ term: SubscriptionTerm; reactivated: boolean }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription-terms/${encodeURIComponent(termId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    )
  },

  /**
   * Cancel a not-yet-effective term — the Billing tab's (Task 7.1) name for
   * `deleteSubscriptionTerm` above. Identical DELETE route; see `updateTerm`
   * for why it is a separate export rather than a rename.
   */
  async deleteTerm(tenantId: string, termId: string) {
    return request<{ ok: true; tenantId: string; termId: string }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription-terms/${encodeURIComponent(termId)}`,
      { method: 'DELETE' },
    )
  },

  /** Task 5.2: the per-term extra/grace spending history behind the Billing
   *  tab's settlement table. */
  async getExtraGraceHistory(tenantId: string) {
    return request<{ rows: ExtraGraceHistoryRow[] }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/extra-grace-history`,
    )
  },

  /**
   * Mark one term's extra spend paid/unpaid (Task 5.2). Takes no amount — the
   * server snapshots whatever `sumExtraGraceByTerm` says the term's extra
   * spend is RIGHT NOW, so a stale client tab cannot settle against a number
   * that has since drifted.
   */
  async setExtraSettlement(tenantId: string, input: { termId: string; paid: boolean }) {
    return request<{ ok: true }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/extra-settlements`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  /**
   * The deep, keyset-paged prepaid-credit ledger (Task 5.3) — the Billing
   * tab's continuation past `adminAccountApi.get`'s fixed 25-row slice
   * (`features/billing/client/credits-admin.ts`). `nextBefore` is `null` once
   * a page comes back shorter than `limit` (the last page); pass it straight
   * back as `before` to fetch the next one.
   */
  async listCreditTransactions(tenantId: string, opts: { before?: string; limit?: number } = {}) {
    const q = new URLSearchParams()
    if (opts.before) q.set('before', opts.before)
    if (opts.limit != null) q.set('limit', String(opts.limit))
    const qs = q.toString()
    return request<{ transactions: CreditTransaction[]; nextBefore: string | null }>(
      `/api/admin/tenants/${encodeURIComponent(tenantId)}/credit-transactions${qs ? `?${qs}` : ''}`,
    )
  },
}

/** The configured fallback, as both manual-billing reads report it. */
export interface ManualFallbackPlan {
  id: string
  name: string | null
  /** Whether this plan pauses uploads — resolved by the same predicate the gate
   *  enforces, because choosing a fallback that does NOT freeze is legitimate and
   *  choosing one that does by accident is not. */
  freezesUploads: boolean
}

/** One row of the overdue queue. */
export interface ManualBillingOverdueRow {
  tenantId: string
  name: string
  slug: string | null
  type: string
  paidThrough: string
  /** Whole days late. Days, not a timestamp — the question is "how late". */
  daysOverdue: number | null
  note: string | null
  planId: string | null
  planName: string | null
  planSource: PlanSource
  /** Already on a plan that pauses uploads, i.e. the fallback has been applied
   *  and this customer is waiting to pay rather than waiting to be acted on. */
  uploadsPaused: boolean
}

export interface ManualBillingQueueResponse {
  planAuthority: PlanAuthority
  plansAreMeaningful: boolean
  fallbackPlan: ManualFallbackPlan | null
  overdue: ManualBillingOverdueRow[]
  /** The queue is bounded. A capped list that did not say so would read as
   *  "that is everyone", and an operator would stop working too early. */
  truncated: boolean
  limit: number
  /**
   * Organizations with NO paid-through date (phase 3).
   *
   * An EMPTY queue is ambiguous without this: it reads as "everyone has paid"
   * when it may mean "nothing is tracked yet". Untracked is a setup state, not a
   * triage state — the answer is the bootstrap script, not a per-customer click.
   */
  untracked?: number
  /**
   * Rule 4: customers carrying a pre-fallback snapshot while NOT on the plan it
   * was taken for. A restore failed part-way, so someone who has paid may still
   * be on fallback ceilings. Named rather than counted — a count says something
   * is wrong without saying who.
   */
  stuckSnapshots?: Array<{
    tenantId: string
    name: string
    fallbackPlanId: string | null
    currentPlanId: string | null
    takenAt: string | null
  }>
}

/** One customer a preset edit could reach. */
/** The five `tenant_settings` money columns, as the route takes them. */
export interface ContractPriceInput {
  /** Minor units of `priceCurrency`. `null` = no flat amount. */
  flatAmountMinor: number | null
  /** Minor units of `priceCurrency`. Inert behind a flat amount. */
  seatPriceMinor: number | null
  /** Lowercase ISO-4217. Required whenever either amount is set. */
  priceCurrency: string | null
  /** Both cycle fields are set together or both null. */
  cycleCount: number | null
  cycleUnit: 'day' | 'week' | 'month' | null
}

export interface PresetApplicableRow {
  tenantId: string
  name: string
  type: string
  /** What their plan DISPLAYS as — "Default", "Custom", or the operator's own
   *  label. Shown so the Custom bucket is legible at a glance. */
  planLabel: string
  customizedAt: string | null
  /**
   * Models this customer can use TODAY that the preset would take away.
   *
   * The one propagation effect a customer notices immediately — and under manual
   * authority it revokes silently, with no upsell to explain the absence. Per
   * tenant, because the answer depends on what they can use now, not on the
   * preset alone.
   */
  losingModels: string[]
  /**
   * Ceilings that differ from the preset's, and would be overwritten.
   *
   * Only DIFFERENCES are sent, so a tenant already matching the preset carries
   * an empty array and the one whose budget is about to halve stands out. Values
   * are the stored ones and the preset's — NOT a simulation of
   * `applyEntitlements`, which combines the plan with promo boosts and would be
   * a second implementation of that resolver.
   */
  changes: Array<{
    /**
     * MUST match the strings the server pushes in `admin-routes.ts`'s
     * `push(...)` calls verbatim.
     *
     * `includedBudgetUsd` lived here until 2026-08-18, four migrations after the
     * currency-native work renamed the emitted string to
     * `includedBudgetBillingMicro`. Nothing failed: the switch in
     * `PropagatePresetDialog`'s `capLabel` matched on the stale name, fell
     * through, and returned `undefined` — which React rendered as the literal
     * text "undefined" as the LABEL of the included-budget change, on the dialog
     * that confirms an irreversible 161-tenant write.
     *
     * Typecheck passed the whole time, because this union was stale in exactly
     * the same way: exhaustiveness checking compares the switch against THIS
     * list, not against the wire. A type that lies about the wire makes the
     * compiler agree with the bug. `preset-apply-labels.test.ts` now pins the
     * two together.
     */
    field: 'includedBudgetBillingMicro' | 'extraCapBillingMicro' | 'storageQuotaBytes' | 'agentConcurrencyCap'
    /** `null` is a real value — unlimited for a money cap, the platform default
     *  for concurrency — and is never the same as `0`. */
    from: number | null
    to: number | null
  }>
}

export interface PresetApplicableResponse {
  planId: string
  planName: string
  audience: string
  standard: PresetApplicableRow[]
  custom: PresetApplicableRow[]
  batchSize: number
}

/** The displaced ceilings a restore would put back (0166). */
export interface PreFallbackView {
  takenAt: string
  fallbackPlanId: string
  /** Restored too — the fifth value. A caps-only restore would say this customer
   *  has never been hand-edited while their ceilings are hand-set. */
  planCustomizedAt: string | null
  caps: {
    storageQuotaBytes: number | string | null
    agentConcurrencyCap: number | string | null
    freeCapBillingMicro: number | string | null
    extraCapBillingMicro: number | string | null
  }
  /** The snapshot exists but the customer is no longer on the plan it was taken
   *  for — a restore failed part-way. Surfaced, never auto-repaired. */
  stuck: boolean
}

export interface ManualBillingResponse {
  planAuthority: PlanAuthority
  plansAreMeaningful: boolean
  paidThrough: string | null
  note: string | null
  status: ManualBillingStatus
  daysOverdue: number | null
  plan: { id: string; name: string; source: PlanSource }
  uploadsPaused: boolean
  fallbackPlan: ManualFallbackPlan | null
  /** What this customer was before the fallback, or `null` when there is
   *  nothing to restore (the normal state). */
  preFallback?: PreFallbackView | null
  /**
   * What to invoice, and which rule produced it (D9 + D13).
   *
   * Resolved SERVER-SIDE, deliberately: the same figure has to be reproducible
   * from the audit trail, and a second copy of the rule in the client is one
   * that drifts from the number the operator actually billed.
   */
  amountDue?: {
    /** MINOR UNITS of `currency`. Never divided here — `formatCurrencyMinor`
     *  does it, because a hardcoded `/100` is a 100× error on JPY. */
    amountMinor: number
    currency: string
    /** Which rule won: a tenant flat amount, a tenant seat price, or the
     *  preset's. Shown, because "why is it this number?" is the question. */
    source: 'tenant-flat' | 'tenant-seat' | 'preset-seat'
    /** A seat price stored behind a flat amount, doing nothing. An operator who
     *  edits it must be told rather than left wondering why the total held. */
    seatPriceInert: boolean
    cycle: { count: number; unit: 'day' | 'week' | 'month' }
    /** The live membership count the seat maths used — NOT the subscription's
     *  cached seat count, which is null for a manually-billed tenant. */
    memberCount: number
  } | null
  /** `'provider'` means applying the fallback is refused — an active
   *  provider-backed subscription outranks a locally-set plan. */
  path: 'provider' | 'local'
  /** The billing currency, so the payment form never guesses and no `/100`
   *  appears in the client. */
  currency: string
}

/** One row of a tenant's `manual_subscription_terms` history (Task 16). */
export interface SubscriptionTerm {
  id: string
  tenantId: string
  planId: string
  /** Midnight UTC of the day it takes effect, as an ISO string. */
  effectiveFrom: string
  /** `null` until this term has taken effect — a future-dated term stays
   *  pending until the sweep (or an immediate append) applies it. */
  appliedAt: string | null
  cycleCount: number
  cycleUnit: SubscriptionCycleUnit
  note: string | null
  createdBy: string | null
  createdAt: string
  /** `current` = what `resolveCurrentTerm` picks for this tenant right now;
   *  `future` = not yet effective; `past` = superseded or historical. */
  status: 'past' | 'current' | 'future'
  /** Operator-entered negotiated amount for this term (migration 0192),
   *  billing-currency MINOR units. `null` = not recorded. */
  paymentAmountMinor: number | null
}

/** One payment recorded against a subscription term — `serializePayment`'s
 *  wire shape (Task 5.1, `manual_subscription_payments`, dates ISO). */
export interface SubscriptionTermPayment {
  id: string
  tenantId: string
  termId: string
  amountMinor: number
  billingCurrency: string
  paidAt: string
  reference: string | null
  note: string | null
  createdBy: string | null
  createdAt: string
}

/**
 * One term joined with its billing derivation — `listTermsWithBilling`'s wire
 * shape (Task 5.1), what the console's Billing-tab subscription-history table
 * reads. Unlike `SubscriptionTerm.status` (past/current/future, positional),
 * this `status` is billing-derived (paid/overdue/untracked/underpaid).
 */
export interface TermBillingRow {
  id: string
  tenantId: string
  planId: string
  effectiveFrom: string
  appliedAt: string | null
  cycleCount: number
  cycleUnit: SubscriptionCycleUnit
  note: string | null
  createdBy: string | null
  createdAt: string
  paymentAmountMinor: number | null
  /** Next term's `effectiveFrom` (newest-first: the term listed above it),
   *  `null` for the term still in force ("present"). */
  endAt: string | null
  /** Σ `payments[].amountMinor`. */
  totalPaidMinor: number
  status: 'paid' | 'overdue' | 'untracked' | 'underpaid'
  /** Current term → next payment due date; `null` when untracked or this is
   *  not the current term. */
  nextPaymentAt: string | null
  /** Whether this is `resolveCurrentTerm`'s row. */
  isCurrent: boolean
  /** This term's payment log, newest-first. */
  payments: SubscriptionTermPayment[]
}

/**
 * One row of `GET /extra-grace-history` (Task 5.2) — a term's window joined
 * with its extra/grace spend and settlement state. Money is billing-currency
 * MICRO (not minor — this ledger shares `sumExtraGraceByTerm`'s unit, not the
 * payments/terms tables' minor units above).
 */
export interface ExtraGraceHistoryRow {
  termId: string
  termStart: string
  termEnd: string | null
  extraBillingMicro: number
  graceBillingMicro: number
  /** When the extra spend for this term was marked paid; `null` = unpaid. */
  extraPaidAt: string | null
}

/**
 * One row of `GET /credit-transactions` (Task 5.3) — the deep, keyset-paged
 * continuation of `adminAccountApi.get`'s fixed 25-row transactions slice
 * (`features/billing/client/credits-admin.ts`), same `usage_credit_transactions`
 * shape plus `actorEmail`.
 */
export interface CreditTransaction {
  id: string
  type: string
  amountBillingMicro: number
  billingCurrency: string
  balanceAfterBillingMicro: number
  ref: string | null
  actorEmail: string | null
  createdAt: string
}
